import React, { ReactElement } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Button } from 'primereact/button'

import {
    ChargePointImport,
    ConnectorImport,
    LoadBalancerGroup,
    LoadBalancerChargePointsImportModel,
    Phase,
    useLoadBalancerGroupImportChargePointsV2Mutation,
} from '../../../../../common/redux/api/web-api'
import useText from '../../../../../common/hooks/useText'
import { isAuraImport } from '../../../../../lib/isAura'
import { isHaloImport } from '../../../../../lib/isHalo'
import { isDawnImport } from '../../../../../lib/isDawn'
import { isLunaImport } from '../../../../../lib/isLuna'
import { EarthingSchemeLB } from '../../../../../common/enums/earthingSchemes'
import { PhaseMode, Phases } from '../../../../../common/domain/electrical-settings'
import { Dialog } from '../../../../../common/uikit/Dialog'
import { useToast } from '../../../../../common/uikit/ToastProvider'

import LoadBalancerOfflineSettingsForm, {
    LoadBalancerOfflineSettingsFormValues,
} from './load-balancer-offline-settings-form'

type LoadBalancerChargePointSettingsDialogProps = {
    chargePoints: Array<ChargePointImport>
    showModal: (show: boolean) => void
    group: LoadBalancerGroup
    updateChargePointList: () => void
}

const formId = 'load-balancer-charge-point-settings-form'

export type OfflineSettings = {
    loadBalancerOfflineCurrent?: number | null
    loadBalancerOfflinePhase?: Phase | null
}

const LoadBalancerChargePointSettingsDialog = ({
    chargePoints,
    showModal,
    group,
    updateChargePointList,
}: LoadBalancerChargePointSettingsDialogProps): ReactElement => {
    const text = useText()
    const toast = useToast()
    const haloChargePoint = chargePoints.filter((chargePoint) => isHaloImport(chargePoint))
    const auraChargePoint = chargePoints.filter((chargePoint) => isAuraImport(chargePoint))
    const dawnChargePoint = chargePoints.filter((chargePoint) => isDawnImport(chargePoint))
    const lunaChargePoint = chargePoints.filter((chargePoint) => isLunaImport(chargePoint))
    const singleCharger = chargePoints?.length === 1

    //Get Min installation current for each charger type
    const minInstallationCurrentHalo = haloChargePoint?.reduce(
        (minCurrent: number | null | undefined, nextChargePoint) => {
            const currentConnector = nextChargePoint.connectors?.find(
                (connector) => connector.connectorId === 1 && connector.installationCurrent
            )

            if (currentConnector) {
                const currentInstallation = currentConnector?.installationCurrent
                if (!minCurrent || (currentInstallation && currentInstallation < minCurrent)) {
                    minCurrent = currentInstallation
                }
            }

            return minCurrent
        },
        undefined
    )

    const minInstallationCurrentAura = auraChargePoint?.reduce(
        (minCurrent: number | null | undefined, nextChargePoint) => {
            const currentConnectors = nextChargePoint.connectors

            if (currentConnectors) {
                currentConnectors.forEach((connector) => {
                    const currentInstallation: number | null | undefined = connector?.installationCurrent

                    if (
                        currentInstallation !== undefined &&
                        (!minCurrent || (currentInstallation && currentInstallation < minCurrent))
                    ) {
                        minCurrent = currentInstallation
                    }
                })
            }

            return minCurrent
        },
        undefined
    )

    const minInstallationCurrentDawn = dawnChargePoint?.reduce(
        (minCurrent: number | null | undefined, nextChargePoint) => {
            const currentConnector = nextChargePoint.connectors?.find(
                (connector) => connector.connectorId === 1 && connector.installationCurrent
            )

            if (currentConnector) {
                const currentInstallation = currentConnector?.installationCurrent
                if (!minCurrent || (currentInstallation && currentInstallation < minCurrent)) {
                    minCurrent = currentInstallation
                }
            }

            return minCurrent
        },
        undefined
    )

    const minInstallationCurrentLuna = lunaChargePoint?.reduce(
        (minCurrent: number | null | undefined, nextChargePoint) => {
            const currentConnector = nextChargePoint.connectors?.find(
                (connector) => connector.connectorId === 1 && connector.installationCurrent
            )

            if (currentConnector) {
                const currentInstallation = currentConnector?.installationCurrent
                if (!minCurrent || (currentInstallation && currentInstallation < minCurrent)) {
                    minCurrent = currentInstallation
                }
            }

            return minCurrent
        },
        undefined
    )

    /**********************************/

    /** IMPORT CHARGEPOINTS TO LB **/
    const [importChargePointsV2, { isLoading: importChargePointLoading }] =
        useLoadBalancerGroupImportChargePointsV2Mutation()

    const ocppChargePoints = chargePoints.find((chargePoint) => chargePoint?.ocppServerMode !== 'None')

    const earthingSystemBasedPhase = group?.earthingSystem === EarthingSchemeLB.IT ? Phases.L1L2 : Phases.L123

    const threePhasesOfflineSettings = (chargePoint: ChargePointImport, connector: ConnectorImport): Phase => {
        return chargePoint.ocppServerMode !== 'None' &&
            connector?.installationPhaseMode === (PhaseMode.threePhase || PhaseMode.twoPhase)
            ? earthingSystemBasedPhase
            : Phases.L1
    }

    const methods = useForm<LoadBalancerOfflineSettingsFormValues>({
        defaultValues: {
            lbc_1_halo:
                chargePoints?.length === 1 && chargePoints[0].connectors
                    ? chargePoints[0].connectors[0]?.loadBalancerOfflineCurrent
                    : group?.minCurrentPerChargePoint,
            lbc_1_dawn:
                chargePoints?.length === 1 && chargePoints[0].connectors
                    ? chargePoints[0].connectors[0]?.loadBalancerOfflineCurrent
                    : group?.minCurrentPerChargePoint,
            lbc_1_aura:
                chargePoints?.length === 1 && chargePoints[0].connectors
                    ? chargePoints[0].connectors[0]?.loadBalancerOfflineCurrent
                    : group?.minCurrentPerChargePoint,

            lbc_2_aura:
                chargePoints?.length === 1 && chargePoints[0].connectors
                    ? chargePoints[0].connectors[1]?.loadBalancerOfflineCurrent
                    : group?.minCurrentPerChargePoint,

            lbOfflinePhase_1:
                chargePoints?.length === 1 && chargePoints[0].connectors
                    ? chargePoints[0].connectors[0]?.loadBalancerOfflinePhase
                    : Phases.L1,

            lbOfflinePhase_2:
                chargePoints?.length === 1 && chargePoints[0].connectors
                    ? chargePoints[0].connectors[1]?.loadBalancerOfflinePhase
                    : Phases.L1,
        },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })

    const getOfflineCurrentValue = (value: string | number | null | undefined): number | null =>
        value === '' || value == null ? null : Number(value)

    const connectorMappingOfflineCurrent = (
        connector: ConnectorImport,
        formValues: LoadBalancerOfflineSettingsFormValues
    ): number | null | undefined => {
        if (connector.name === 'Socket' && connector.connectorId === 2) {
            return group?.minCurrentPerChargePoint
        }
        if (connector.name === 'Socket') {
            return getOfflineCurrentValue(formValues.lbc_1_dawn)
        }
        if (connector.name === 'Charger') {
            return getOfflineCurrentValue(formValues.lbc_1_halo)
        }
        if (connector.name === 'Left socket') {
            return getOfflineCurrentValue(formValues.lbc_1_aura)
        }
        if (connector.name === 'Right socket') {
            return getOfflineCurrentValue(formValues.lbc_2_aura)
        }

        return null
    }

    const connectorMappingOfflinePhase = (
        connector: ConnectorImport,
        formValues: LoadBalancerOfflineSettingsFormValues
    ): Phase => {
        return connector?.connectorId === 1
            ? (formValues.lbOfflinePhase_1 as Phase) ?? Phases.L1
            : (formValues.lbOfflinePhase_2 as Phase) ?? Phases.L1
    }

    const doImportChargePointsV2: SubmitHandler<LoadBalancerOfflineSettingsFormValues> = async (formValues) => {
        const updatedChargePoints = chargePoints?.map((chargePoint: ChargePointImport) => ({
            ...chargePoint,
            hasError: false,
            errorReason: [],

            connectors: chargePoint.connectors?.map((connector) => ({
                ...connector,
                ...(connector && {
                    loadBalancerOfflinePhase:
                        chargePoints?.length === 1
                            ? connectorMappingOfflinePhase(connector, formValues)
                            : threePhasesOfflineSettings(chargePoint, connector),
                    loadBalancerOfflineCurrent:
                        connectorMappingOfflineCurrent(connector, formValues) ?? group?.minCurrentPerChargePoint,
                }),
            })),
        }))

        const importChargers: LoadBalancerChargePointsImportModel = {
            isAutorotate: chargePoints.length > 1 && !ocppChargePoints,
            chargePoints: updatedChargePoints,
        }

        if (group?.id) {
            try {
                await importChargePointsV2({
                    id: group.id,
                    loadBalancerChargePointsImportModel: importChargers,
                }).unwrap()

                showModal(false)
                toast.notifySuccess(text('lblLoadbalancerImportSuccess'))
                updateChargePointList()
            } catch (error) {
                showModal(false)
                toast.notifyError(text('lblLoadbalancerImportFail'))
            }
        }
    }

    /************************************************/

    const isSaveDisabled = methods.formState.isSubmitted && !methods.formState.isValid
    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" outlined onClick={() => showModal(false)} />
            <Button
                label={text('btnSave')}
                type="submit"
                form={formId}
                disabled={isSaveDisabled}
                loading={importChargePointLoading}
            />
        </div>
    )

    return (
        <Dialog
            header={text('lblLoadBalancerSettingsChargePoint')}
            visible
            onHide={() => showModal(false)}
            footer={footer}
            style={{ width: '50rem' }}
        >
            <form
                id={formId}
                className="flex flex-column"
                noValidate
                onSubmit={methods.handleSubmit(doImportChargePointsV2)}
            >
                {haloChargePoint.length > 0 && (
                    <>
                        {chargePoints.length === 1 && (
                            <div className="flex flex-column gap-1 mb-3" data-testid="halo-charger-info">
                                <span className="text-base">{haloChargePoint[0].id}</span>
                                <span className="text-sm text-600">{haloChargePoint[0].name}</span>
                            </div>
                        )}
                        {chargePoints.length > 1 && (
                            <div className="mb-3">
                                <h3 className="text-base m-0 font-semibold">
                                    {text('lblLoadBalancerSettingsChargePointHalo')}
                                </h3>
                            </div>
                        )}

                        <LoadBalancerOfflineSettingsForm
                            chargerType="halo"
                            singleCharger={singleCharger}
                            chargePoint={haloChargePoint[0]}
                            minInstallationCurrent={minInstallationCurrentHalo ?? 0}
                            earthingSystem={group?.earthingSystem}
                            control={methods.control}
                            errors={methods.formState.errors}
                        />
                    </>
                )}
                {dawnChargePoint.length > 0 && (
                    <>
                        {chargePoints.length === 1 && (
                            <div className="flex flex-column gap-1 mb-3">
                                <span className="text-base">{dawnChargePoint[0].id}</span>
                                <span className="text-sm text-600">{dawnChargePoint[0].name}</span>
                            </div>
                        )}
                        {chargePoints.length > 1 && (
                            <div className="mb-3">
                                <h3 className="text-base m-0 font-semibold">
                                    {text('lblLoadBalancerSettingsChargePointDawn')}
                                </h3>
                            </div>
                        )}

                        <LoadBalancerOfflineSettingsForm
                            chargerType="dawn"
                            singleCharger={singleCharger}
                            chargePoint={dawnChargePoint[0]}
                            minInstallationCurrent={minInstallationCurrentDawn ?? 0}
                            earthingSystem={group?.earthingSystem}
                            control={methods.control}
                            errors={methods.formState.errors}
                        />
                    </>
                )}

                {lunaChargePoint.length > 0 && (
                    <>
                        {chargePoints.length === 1 && (
                            <div className="flex flex-column gap-1 mb-3">
                                <span className="text-base">{lunaChargePoint[0].id}</span>
                                <span className="text-sm text-600">{lunaChargePoint[0].name}</span>
                            </div>
                        )}
                        {chargePoints.length > 1 && (
                            <div className="mb-3">
                                <h3 className="text-base m-0 font-semibold">
                                    {text('lblLoadBalancerSettingsChargePointLuna')}
                                </h3>
                            </div>
                        )}

                        <LoadBalancerOfflineSettingsForm
                            chargerType="luna"
                            singleCharger={singleCharger}
                            chargePoint={lunaChargePoint[0]}
                            minInstallationCurrent={minInstallationCurrentLuna ?? 0}
                            earthingSystem={group?.earthingSystem}
                            control={methods.control}
                            errors={methods.formState.errors}
                        />
                    </>
                )}

                {auraChargePoint.length > 0 && (
                    <>
                        {chargePoints.length === 1 && (
                            <div className="flex flex-column gap-1 mb-3">
                                <span className="text-base">{auraChargePoint[0].id}</span>
                                <span className="text-sm text-600">{auraChargePoint[0].name}</span>
                            </div>
                        )}
                        {chargePoints.length > 1 && (
                            <div className="mb-3">
                                <h3 className="text-base m-0 font-semibold">
                                    {text('lblLoadBalancerSettingsChargePointAura')}
                                </h3>
                            </div>
                        )}

                        <LoadBalancerOfflineSettingsForm
                            chargerType="aura"
                            singleCharger={singleCharger}
                            chargePoint={auraChargePoint[0]}
                            minInstallationCurrent={minInstallationCurrentAura ?? 0}
                            earthingSystem={group?.earthingSystem}
                            control={methods.control}
                            errors={methods.formState.errors}
                        />
                    </>
                )}
            </form>
        </Dialog>
    )
}

export default LoadBalancerChargePointSettingsDialog
